import dayjs from 'dayjs';

/**
 * Align a date to the start of its bucket for a given granularity.
 * - DAY: same calendar day (00:00 UTC)
 * - WEEK: ISO week start (Monday)
 * - MONTH: first day of month
 */
const alignToBucket = (date, granularity) => {
  const d = dayjs(date);
  switch (granularity) {
    case 'DAY':
      return d.startOf('day');
    case 'WEEK':
      // dayjs default week starts on Sunday; ISO weeks start Monday.
      // Use day() to shift: Monday = 1.
      return d.startOf('day').subtract((d.day() + 6) % 7, 'day');
    case 'MONTH':
      return d.startOf('month');
    default:
      throw new Error(`Unknown granularity: ${granularity}`);
  }
};

const advance = (d, granularity) => {
  switch (granularity) {
    case 'DAY':
      return d.add(1, 'day');
    case 'WEEK':
      return d.add(1, 'week');
    case 'MONTH':
      return d.add(1, 'month');
    default:
      throw new Error(`Unknown granularity: ${granularity}`);
  }
};

/**
 * Fill missing buckets between `from` and `to` (inclusive of from-aligned bucket,
 * up to to-aligned bucket) with zero-count entries so the X axis is contiguous.
 *
 * @param {Array<{ bucket: string|Date, count: number }>} rows  - sparse server rows
 * @param {string|Date} from
 * @param {string|Date} to
 * @param {'DAY'|'WEEK'|'MONTH'} granularity
 * @returns {Array<{ bucket: string, count: number }>}  bucket as ISO YYYY-MM-DD
 */
const fillBuckets = (rows, from, to, granularity) => {
  const byKey = new Map();
  (rows || []).forEach(({ bucket, count }) => {
    const key = alignToBucket(bucket, granularity).format('YYYY-MM-DD');
    byKey.set(key, count);
  });

  const out = [];
  const end = alignToBucket(to, granularity);
  let cursor = alignToBucket(from, granularity);

  // Safety cap to prevent runaway loops on bad input.
  let guard = 0;
  while (cursor.isSame(end) || cursor.isBefore(end)) {
    const key = cursor.format('YYYY-MM-DD');
    out.push({ bucket: key, count: byKey.get(key) || 0 });
    cursor = advance(cursor, granularity);
    guard += 1;
    if (guard > 10000) {
      throw new Error('fillBuckets: bucket count exceeded safety cap');
    }
  }

  return out;
};

export default fillBuckets;
